import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import { BATCH_TRANSLATE_REVIEWS_MUTATION } from "@/graphql/google-business";
import i18n from "@/lib/i18n/config";

export function useReviewTranslation() {
  const [batchTranslate, { loading: isTranslating }] = useMutation(
    BATCH_TRANSLATE_REVIEWS_MUTATION
  );

  const currentLanguage = i18n.language;

  const translateInBackground = useCallback(
    (
      reviews: Array<{
        id: string;
        salonId: string;
        starRating: number;
        comment?: string | null;
        translationChecked: boolean;
        google?: { replyComment?: string | null } | null;
      }>,
      salonId: string
    ) => {
      const eligible = reviews.filter(
        (r) =>
          r.starRating <= 3 &&
          r.comment &&
          r.comment.trim().length > 0 &&
          !r.translationChecked &&
          !r.google?.replyComment
      );

      if (eligible.length === 0) return;

      const eligibleIds = eligible.map((r) => r.id);

      batchTranslate({
        variables: {
          salonId,
          reviewIds: eligibleIds,
          targetLanguage: currentLanguage,
        },
        update(cache, { data }) {
          const translations = data?.batchTranslateReviews?.translations ?? [];
          for (const t of translations) {
            cache.modify({
              id: cache.identify({ __typename: "SalonReview", id: t.id }),
              fields: {
                translationChecked: () => true,
                translatedComment: () => t.translatedComment,
              },
            });
          }
          const translatedIds = new Set(translations.map((t) => t.id));
          for (const id of eligibleIds) {
            if (!translatedIds.has(id)) {
              cache.modify({
                id: cache.identify({ __typename: "SalonReview", id }),
                fields: {
                  translationChecked: () => true,
                },
              });
            }
          }
        },
      }).catch(() => {
        // Silently ignore translation failures — non-critical background task.
      });
    },
    [batchTranslate]
  );

  return { translateInBackground, isTranslating };
}
